import { supabase, requireSession } from "@/lib/supabase/client";
import type { Json } from "@/lib/supabase/database.types";
import type { EventProfile } from "@/types";
import { nextClientRevision, reliableWrite, rememberClientRevision } from "@/lib/persistence/reliableWrite";

/**
 * Event Service — reads and writes EventProfile to Supabase.
 *
 * The entire EventProfile (including the events array) is stored as JSONB.
 * One row per user, upserted on every save.
 */
export const eventService = {
  /**
   * Load the event profile for the current user.
   * Returns null if the user hasn't set any events yet.
   */
  async load(userId: string): Promise<EventProfile | null> {
    const { data, error } = await supabase
      .from("events")
      .select("data")
      .eq("user_id", userId)
      .single();

    // PGRST116 = "no rows found" — not an error in our case
    if (error?.code === "PGRST116") return null;
    if (error) throw new Error(`[eventService.load] ${error.message}`);

    const event = data?.data ? (data.data as unknown as EventProfile) : null;
    if (event) rememberClientRevision(`events:${userId}`, event.revision ?? 0);
    return event;
  },

  /**
   * Save (upsert) the full event profile for the current user.
   */
  async save(userId: string, eventProfile: EventProfile): Promise<void> {
    await requireSession(userId);
    return reliableWrite(`events:${userId}`, async () => {
    const versioned = {
      ...eventProfile,
      revision: nextClientRevision(`events:${userId}`, eventProfile.revision),
    };
    const { data: accepted, error: rpcError } = await supabase.rpc("save_event_if_newer", {
      p_user_id: userId,
      p_data: versioned as unknown as Json,
    });
    if (!rpcError && accepted === true) return;
    if (!rpcError && accepted === false) throw new Error("A newer race calendar was saved on another device. Reload before trying again.");
    // Fall through during rolling migration; the existing RLS upsert remains safe.
    const { error } = await supabase.from("events").upsert(
      {
        user_id: userId,
        data: versioned as unknown as Json,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" },
    );

    if (error)
      throw new Error(
        `[eventService.save] ${error.message} (code: ${error.code}; rpc: ${rpcError?.code ?? "empty-response"})${
          error.hint ? ` — ${error.hint}` : ""
        }`,
      );
    });
  },
};
